import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Gift, Phone, Check, ChevronDown, ChevronUp, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SorteoPopup = ({ onClose, onParticipated }) => {
  const [config, setConfig] = useState(null);
  const [phone, setPhone] = useState('');
  const [acceptBases, setAcceptBases] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [showBases, setShowBases] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/turbo-marketing/sorteo/current`)
      .then(r => r.json())
      .then(d => { if (d.is_active) setConfig(d); else onClose(); })
      .catch(() => onClose());
  }, [onClose]);

  const handleSubmit = async () => {
    setError('');
    if (!phone || phone.trim().length < 6) { setError('Introduce tu teléfono'); return; }
    if (!acceptBases) { setError('Debes aceptar las bases legales'); return; }
    if (!acceptPrivacy) { setError('Debes aceptar la política de privacidad'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/sorteo/participate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), accept_bases: true, accept_privacy: true, accept_marketing: acceptMarketing }),
      });
      if (res.ok) {
        setDone(true);
        if (onParticipated) onParticipated();
      } else {
        const data = await res.json();
        setError(data.detail || 'Error al participar');
      }
    } catch (e) {
      setError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) return null;

  const imageUrl = config.image_url
    ? (config.image_url.startsWith('/api') ? `${API_URL}${config.image_url}` : config.image_url)
    : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" data-testid="sorteo-popup">
      <div className="w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-4 duration-300 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--sand-50)' }}>

        {/* Image */}
        {imageUrl && (
          <div className="relative h-40 overflow-hidden">
            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors z-10"
          data-testid="sorteo-close-btn"
        >
          <X size={18} />
        </button>

        <div className="p-5">
          {done ? (
            /* Success state */
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(183,140,60,0.15)' }}>
                <Check size={32} style={{ color: 'var(--gold)' }} />
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--stone-800)' }}>Participación registrada</h3>
              <p className="text-sm mb-6" style={{ color: 'var(--stone-500)' }}>Mucha suerte en el sorteo</p>
              <Button onClick={onClose} className="w-full py-4 rounded-full text-white" style={{ backgroundColor: 'var(--gold)' }} data-testid="sorteo-done-btn">
                Cerrar
              </Button>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center gap-2 mb-1">
                <Gift size={18} style={{ color: 'var(--gold)' }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--gold-dark)' }}>{config.sorteo_name}</span>
              </div>
              <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--stone-800)' }}>{config.title}</h3>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--gold)' }}>{config.subtitle}</p>
              <p className="text-sm mb-5" style={{ color: 'var(--stone-500)' }}>{config.description}</p>

              {/* Phone */}
              <div className="space-y-1.5 mb-4">
                <label className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--stone-600)' }}>
                  <Phone size={14} /> Teléfono
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+34 600 000 000"
                  className="w-full rounded-xl px-4 py-3 text-base focus:outline-none"
                  style={{ border: '1px solid rgba(183,140,60,0.25)', backgroundColor: 'var(--sand-50)' }}
                  data-testid="sorteo-phone-input"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 mb-4">
                {/* Bases legales */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptBases} onChange={e => setAcceptBases(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded" style={{ accentColor: 'var(--gold)' }} data-testid="accept-bases" />
                  <span className="text-sm" style={{ color: 'var(--stone-600)' }}>
                    Acepto las{' '}
                    <button type="button" onClick={() => setShowBases(!showBases)} className="underline" style={{ color: 'var(--gold)' }}>
                      bases legales del sorteo
                    </button>
                  </span>
                </label>
                {showBases && config.bases_legales && (
                  <div className="ml-8 p-3 rounded-lg text-xs max-h-40 overflow-y-auto whitespace-pre-wrap" style={{ backgroundColor: 'var(--sand-100)', color: 'var(--stone-500)' }}>
                    {config.bases_legales}
                  </div>
                )}

                {/* Política privacidad */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={acceptPrivacy} onChange={e => setAcceptPrivacy(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded" style={{ accentColor: 'var(--gold)' }} data-testid="accept-privacy" />
                  <span className="text-sm" style={{ color: 'var(--stone-600)' }}>
                    He leído la{' '}
                    <button type="button" onClick={() => setShowPrivacy(!showPrivacy)} className="underline" style={{ color: 'var(--gold)' }}>
                      política de privacidad
                    </button>
                  </span>
                </label>
                {showPrivacy && config.politica_privacidad && (
                  <div className="ml-8 p-3 rounded-lg text-xs max-h-40 overflow-y-auto whitespace-pre-wrap" style={{ backgroundColor: 'var(--sand-100)', color: 'var(--stone-500)' }}>
                    {config.politica_privacidad}
                  </div>
                )}

                {/* Marketing opt-in */}
                <div className="pt-2 border-t" style={{ borderColor: 'rgba(183,140,60,0.15)' }}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={acceptMarketing} onChange={e => setAcceptMarketing(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded" style={{ accentColor: 'var(--gold)' }} data-testid="accept-marketing" />
                    <div>
                      <span className="text-sm font-medium" style={{ color: 'var(--stone-800)' }}>{config.marketing_checkbox_text}</span>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--stone-400)' }}>{config.marketing_sub_text}</p>
                    </div>
                  </label>
                </div>
              </div>

              {error && <p className="text-sm text-red-500 mb-3" data-testid="sorteo-error">{error}</p>}

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full py-5 rounded-full text-base font-semibold text-white border-0"
                style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}
                data-testid="sorteo-submit-btn"
              >
                {submitting ? 'Participando...' : 'Participar en el sorteo'}
              </Button>

              {/* Skip */}
              <button onClick={onClose} className="w-full py-2.5 mt-2 text-sm transition-colors" style={{ color: 'var(--stone-400)' }} data-testid="sorteo-skip-btn">
                No, gracias
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SorteoPopup;
